/**
 * Cutting the photographs out of a scanned album page.
 *
 * The detection has been running since the container was built — `page_regions`
 * holds the rectangles it found on every page it looked at — and nothing ever
 * offered to use them. So an inherited album of twelve pages stayed twelve
 * photographs *of pages*, rather than becoming the sixty photographs that are
 * on them, and none of those sixty could be dated, named or found by searching
 * because as far as the archive was concerned they did not exist.
 *
 * ## The rectangles are editable
 *
 * The detector finds most prints on most pages and misses some: two snapshots
 * that overlap come back as one rectangle, and a print on a busy patterned
 * mount comes back as none. Offering only its answer means accepting a bad
 * split or getting nothing, so every rectangle can be moved, resized or
 * deleted, and new ones can be drawn on the empty parts of the page. What gets
 * cut is what is on the screen.
 */

import { h, clear } from "./dom";
import { api } from "./api";
import { store } from "./state";
import { toast } from "./ui";
import { count } from "./format";
import type { AssetCard } from "./types";

interface Rect {
  x: number;
  y: number;
  w: number;
  h: number;
  /**
   * The print's four corners, once somebody has moved one.
   *
   * A print lying on a scrapbook page is rarely square to the glass, and a
   * phone held over an album is never square to the page — so a rectangle cut
   * out of either is a rectangle full of a trapezium, with the top edge
   * shorter than the bottom and the verticals leaning. That is not something a
   * rectangle can fix, because the problem is not where the photograph is but
   * what shape it is.
   *
   * Absent until a corner is dragged, so a page that was scanned flat stays on
   * the cheap rectangular path and nobody has to think about corners they do
   * not need.
   */
  quad?: { tl: Corner; tr: Corner; br: Corner; bl: Corner };
}

type Corner = [number, number];

/** The corners a rectangle implies, for when somebody first grabs one. */
function quadOf(r: Rect): { tl: Corner; tr: Corner; br: Corner; bl: Corner } {
  return (
    r.quad ?? {
      tl: [r.x, r.y],
      tr: [r.x + r.w, r.y],
      br: [r.x + r.w, r.y + r.h],
      bl: [r.x, r.y + r.h],
    }
  );
}

/** The rectangle a quad sits inside, so the label and the hit area still work. */
function boundsOf(q: { tl: Corner; tr: Corner; br: Corner; bl: Corner }): Rect {
  const xs = [q.tl[0], q.tr[0], q.br[0], q.bl[0]];
  const ys = [q.tl[1], q.tr[1], q.br[1], q.bl[1]];
  const x = Math.min(...xs);
  const y = Math.min(...ys);
  return { x, y, w: Math.max(...xs) - x, h: Math.max(...ys) - y };
}

/** Below this a rectangle is a stray drag rather than a photograph. */
const MIN_EDGE = 0.02;

export interface PageSplit {
  el: HTMLElement;
  /** The overlay that goes on top of the photograph. */
  overlay: HTMLElement;
}

export function pageSplit(a: AssetCard, onDone: () => void): PageSplit | null {
  if (!store.can("asset.edit")) return null;

  const overlay = h("div", { class: "psoverlay", hidden: true });
  const el = h("div", { class: "pagesplit", hidden: true });
  let rects: Rect[] = [];
  let busy = false;

  const norm = (r: Rect): Rect => ({
    x: Math.max(0, Math.min(1, Math.min(r.x, r.x + r.w))),
    y: Math.max(0, Math.min(1, Math.min(r.y, r.y + r.h))),
    w: Math.min(1, Math.abs(r.w)),
    h: Math.min(1, Math.abs(r.h)),
  });

  const paint = (): void => {
    clear(overlay);
    rects.forEach((r, i) => {
      const q = quadOf(r);
      const b = boundsOf(q);
      const box = h("div", {
        class: r.quad ? "psrect skewed" : "psrect",
        style: `left:${b.x * 100}%;top:${b.y * 100}%;width:${b.w * 100}%;height:${b.h * 100}%`,
      });

      // The outline follows the corners, not the box. On a keystoned print the
      // two differ by a good fraction of the page, and a rectangle drawn round
      // a trapezium tells somebody the wrong thing about what they will get.
      if (r.quad) {
        const pct = (c: Corner): string =>
          `${((c[0] - b.x) / (b.w || 1)) * 100}% ${((c[1] - b.y) / (b.h || 1)) * 100}%`;
        box.style.clipPath = `polygon(${pct(q.tl)}, ${pct(q.tr)}, ${pct(q.br)}, ${pct(q.bl)})`;
      }

      box.appendChild(h("span", { class: "psnum", text: String(i + 1) }));
      box.appendChild(
        h("button", {
          class: "psdel",
          text: "×",
          title: "Not a photograph — remove this rectangle",
          onpointerdown: (e: PointerEvent) => e.stopPropagation(),
          onclick: (e: MouseEvent) => {
            e.stopPropagation();
            rects.splice(i, 1);
            paint();
            say();
          },
        }),
      );

      // Moving the whole thing: anywhere on the shape that is not a corner.
      box.addEventListener("pointerdown", (ev: PointerEvent) => {
        ev.preventDefault();
        ev.stopPropagation();
        const rect = overlay.getBoundingClientRect();
        const startX = ev.clientX;
        const startY = ev.clientY;
        const from = quadOf(r);
        const move = (m: PointerEvent): void => {
          const dx = (m.clientX - startX) / rect.width;
          const dy = (m.clientY - startY) / rect.height;
          const shift = (c: Corner): Corner => [
            Math.max(0, Math.min(1, c[0] + dx)),
            Math.max(0, Math.min(1, c[1] + dy)),
          ];
          if (r.quad) {
            rects[i] = { ...r, quad: { tl: shift(from.tl), tr: shift(from.tr), br: shift(from.br), bl: shift(from.bl) } };
            Object.assign(rects[i]!, boundsOf(rects[i]!.quad!));
          } else {
            rects[i] = norm({ ...r, x: r.x + dx, y: r.y + dy });
          }
          paint();
        };
        const up = (): void => {
          window.removeEventListener("pointermove", move);
          window.removeEventListener("pointerup", up);
          say();
        };
        window.addEventListener("pointermove", move);
        window.addEventListener("pointerup", up);
      });

      overlay.appendChild(box);

      // The corners themselves, outside the clipped shape so they stay
      // grabbable however skewed it gets.
      (["tl", "tr", "br", "bl"] as const).forEach((which) => {
        const c = q[which];
        const grip = h("span", {
          class: `pscorner ${which}`,
          style: `left:${c[0] * 100}%;top:${c[1] * 100}%`,
          title: "Drag to the corner of the photograph — straightens a print that is not square to the page",
        });
        grip.addEventListener("pointerdown", (ev: PointerEvent) => {
          ev.preventDefault();
          ev.stopPropagation();
          const rect = overlay.getBoundingClientRect();
          const from = quadOf(r);
          const move = (m: PointerEvent): void => {
            const nx = Math.max(0, Math.min(1, (m.clientX - rect.left) / rect.width));
            const ny = Math.max(0, Math.min(1, (m.clientY - rect.top) / rect.height));
            const next = { ...from, [which]: [nx, ny] as Corner };
            rects[i] = { ...boundsOf(next), quad: next };
            paint();
          };
          const up = (): void => {
            window.removeEventListener("pointermove", move);
            window.removeEventListener("pointerup", up);
            say();
          };
          window.addEventListener("pointermove", move);
          window.addEventListener("pointerup", up);
        });
        overlay.appendChild(grip);
      });
    });
  };

  // Drawing a new rectangle on an empty part of the page.
  overlay.addEventListener("pointerdown", (ev: PointerEvent) => {
    if (busy) return;
    ev.preventDefault();
    const rect = overlay.getBoundingClientRect();
    const x0 = (ev.clientX - rect.left) / rect.width;
    const y0 = (ev.clientY - rect.top) / rect.height;
    const index = rects.length;
    rects.push({ x: x0, y: y0, w: 0, h: 0 });

    const move = (m: PointerEvent): void => {
      rects[index] = norm({
        x: x0,
        y: y0,
        w: (m.clientX - rect.left) / rect.width - x0,
        h: (m.clientY - rect.top) / rect.height - y0,
      });
      paint();
    };
    const up = (): void => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
      // A click rather than a drag leaves a rectangle of nothing.
      const drawn = rects[index];
      if (!drawn || drawn.w < MIN_EDGE || drawn.h < MIN_EDGE) rects.splice(index, 1);
      paint();
      say();
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
  });

  const line = h("p", { class: "psline" });
  const say = (): void => {
    line.textContent = rects.length
      ? `${count(rects.length, "photograph")} on this page. Drag a corner onto ` +
        "the corner of the print — that straightens one lying at an angle. " +
        "Drag the middle to move it, or drag on the page to add another."
      : "No rectangles. Drag on the page to draw one.";
  };

  const buttons = h("div", { class: "psacts" });

  const build = (): void => {
    clear(buttons);
    buttons.appendChild(
      h("button", {
        class: "btn sm primary",
        text: "Cut them out",
        onclick: () => {
          void (async () => {
            if (busy || !rects.length) return;
            busy = true;
            try {
              // Corners only where somebody moved one. A page scanned flat
              // stays on the rectangular path, which needs no resampling at
              // all — a warp of an already-square print is a copy that costs
              // sharpness for nothing.
              const r = await api.extractPage(
                store.libraryId,
                a.id,
                rects.map((x) => ({
                  x: x.x,
                  y: x.y,
                  w: x.w,
                  h: x.h,
                  ...(x.quad ? { quad: x.quad } : {}),
                })),
              );
              toast(
                r.failed.length
                  ? `Made ${count(r.created, "photograph")}; ${r.failed.length} would not cut.`
                  : `Made ${count(r.created, "photograph")}. The page is kept, hidden.`,
              );
              onDone();
            } catch (e) {
              toast(e instanceof Error ? e.message : "could not split it", true);
            } finally {
              busy = false;
            }
          })();
        },
      }),
    );
    buttons.appendChild(
      h("button", {
        class: "btn sm ghost",
        text: "Leave it whole",
        title: "This is one photograph, not a page of them — stop asking",
        onclick: () => {
          void api
            .keepPageWhole(store.libraryId, a.id)
            .then(() => {
              toast("Left whole.");
              onDone();
            })
            .catch((e: unknown) =>
              toast(e instanceof Error ? e.message : "could not save that", true),
            );
        },
      }),
    );
  };

  void api
    .pageRegions(store.libraryId, a.id)
    .then((found) => {
      // Nothing to offer: not a page, or somebody has already decided about
      // it. A panel that appears on every photograph is a panel people learn
      // to ignore.
      if (!found.regions.length || found.reviewedAt) return;
      rects = found.regions.map((r) => ({ x: r.x, y: r.y, w: r.w, h: r.h }));
      overlay.hidden = false;
      el.hidden = false;
      clear(el);
      el.appendChild(
        h("div", { class: "sectlbl", text: `Looks like ${count(rects.length, "photograph")} on a page` }),
      );
      el.appendChild(line);
      el.appendChild(buttons);
      build();
      paint();
      say();
    })
    .catch(() => {
      /* not a page, or the detector has not looked yet */
    });

  return { el, overlay };
}
